import {
  BookOpen,
  CalendarDays,
  FileText,
  Home,
  Mail,
  MapPin,
  Menu,
  Newspaper,
  Phone,
  Search,
  Users,
  X,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useIsFetching, useQuery } from '@tanstack/react-query'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { school } from '../data/mockData'
import { api } from '../services/api'
import { ChatBot } from './ChatBot'
import { SchoolLogo } from './SchoolLogo'

const mainNav = [
  { label: 'TRANG CHỦ', to: '/' },
  { label: 'GIỚI THIỆU', to: '/gioi-thieu' },
  { label: 'TIN TỨC', to: '/tin-tuc' },
  { label: 'ĐÀO TẠO', to: '/dao-tao' },
  { label: 'HOẠT ĐỘNG', to: '/tin-tuc?category=Hoạt động đoàn đội' },
  { label: 'THƯ VIỆN', to: '/thu-vien' },
  { label: 'THÔNG BÁO', to: '/thong-bao' },
  { label: 'VĂN BẢN', to: '/van-ban' },
  { label: 'LIÊN HỆ', to: '/lien-he' },
]

const shortcuts = [
  { icon: Users, label: 'GIỚI THIỆU', note: 'Về nhà trường', to: '/gioi-thieu' },
  { icon: Newspaper, label: 'TIN TỨC', note: 'Sự kiện nổi bật', to: '/tin-tuc' },
  { icon: FileText, label: 'VĂN BẢN', note: 'Công văn - Thông báo', to: '/van-ban' },
  { icon: BookOpen, label: 'THƯ VIỆN', note: 'Tài liệu - Học liệu', to: '/thu-vien' },
]

const loadingHideDelay = Number.parseInt(import.meta.env.VITE_LOADING_HIDE_DELAY_MS || '2000', 10)
const loadingHideDelayMs = Number.isFinite(loadingHideDelay) && loadingHideDelay >= 0 ? loadingHideDelay : 2000

export function Layout() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [term, setTerm] = useState('')
  const navigate = useNavigate()
  const pendingFetches = useIsFetching({
    predicate: (query) => query.state.status === 'pending' && query.state.fetchStatus === 'fetching',
  })
  const location = useLocation()
  const isAboutLanding = location.pathname === '/gioi-thieu'
  const isHomePage = location.pathname === '/'
  const { data: siteConfig } = useQuery({
    queryKey: ['site-config'],
    queryFn: () => api.getSiteConfig(),
  })

  const site = siteConfig?.site
  const schoolName = site?.schoolName ?? site?.name ?? school.name
  const schoolAuthority = site?.educationAuthority ?? 'UBND PHƯỜNG QUẢNG PHÚ'
  const schoolSlogan = site?.slogan ?? school.slogan
  const schoolAddress = site?.address ?? school.address
  const schoolEmail = site?.email ?? school.email
  const schoolPhone = site?.phone ?? school.phone
  const workingHours = site?.workingHours ?? 'Thứ 2 - Thứ 6 (07:00 - 17:00)'
  const logoUrl = site?.logo?.url
  const displayLogoUrl = logoUrl?.includes('nguyen-khuyen') ? undefined : logoUrl
  const footer = siteConfig?.footer
  const footerDescription = footer?.description || 'Nhà trường không ngừng đổi mới, sáng tạo trong dạy và học, hướng tới xây dựng môi trường giáo dục thân thiện, học sinh tích cực.'
  const footerColumns = footer?.columns?.filter((column) => column.title && column.links?.length) ?? [
    {
      title: 'VỀ NHÀ TRƯỜNG',
      links: [
        { label: 'Giới thiệu chung', url: '/gioi-thieu' },
        { label: 'Ban giám hiệu', url: '/gioi-thieu/ve-nha-truong' },
        { label: 'Cơ cấu tổ chức', url: '/gioi-thieu/ve-nha-truong' },
        { label: 'Thành tích nhà trường', url: '/gioi-thieu/ve-nha-truong' },
        { label: 'Kế hoạch phát triển', url: '/dao-tao' },
      ],
    },
    {
      title: 'HỖ TRỢ',
      links: [
        { label: 'Hướng dẫn sử dụng', url: '/huong-dan' },
        { label: 'Câu hỏi thường gặp', url: '/cau-hoi' },
        { label: 'Điều khoản sử dụng', url: '/dieu-khoan' },
        { label: 'Chính sách bảo mật', url: '/chinh-sach' },
      ],
    },
    {
      title: 'DÀNH CHO',
      links: [
        { label: 'Phụ huynh', url: '/thong-bao' },
        { label: 'Học sinh', url: '/thu-vien' },
        { label: 'Giáo viên', url: '/tin-tuc' },
      ],
    },
  ]
  const footerCopyright = footer?.copyright || '© 2026 Trường THCS Lý Thường Kiệt.'
  const footerDesigner = footer?.designer || 'Thiết kế bởi NDHoa'

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault()
    if (term.trim()) navigate(`/tim-kiem?q=${encodeURIComponent(term.trim())}`)
    setSearchOpen(false)
  }

  const isNavActive = (to: string) => {
    const [pathname, search = ''] = to.split('?')
    if (pathname === '/') return location.pathname === '/'
    if (search) return location.pathname === pathname && location.search === `?${search}`
    return location.pathname === pathname && !location.search
  }

  return (
    <div className={`site-shell ${!isHomePage ? 'site-shell--inner-page' : ''} ${isAboutLanding ? 'site-shell--about-landing' : ''}`}>
      <PageLoadingOverlay
        active={pendingFetches > 0}
        authority={schoolAuthority}
        name={schoolName}
        slogan={schoolSlogan}
        logoUrl={displayLogoUrl}
      />
      <header>
        <div className="topbar">
          <div className="container topbar__inner">
            <div className="topbar__contact">
              <span><MapPin size={14} />{schoolAddress}</span>
              <span><Mail size={14} />{schoolEmail}</span>
              {/* <span><Phone size={14} />{schoolPhone}</span> */}
            </div>
            <div className="topbar__links">
              <Link to="/sitemap">Sitemap</Link><span>|</span><a href="https://apithcs.nhadan.dedyn.io/admin">Đăng nhập</a>
              <button className="topbar-search" onClick={() => setSearchOpen(true)}><span>Tìm kiếm...</span><Search size={17} /></button>
            </div>
          </div>
        </div>
        <div className="brandbar">
          <div className="container brandbar__inner">
            <Link to="/" aria-label="Trang chủ"><SchoolLogo authority={schoolAuthority} name={schoolName} slogan={schoolSlogan} logoUrl={displayLogoUrl} /></Link>
            <div className="header-shortcuts">
              {shortcuts.map(({ icon: Icon, ...item }) => (
                <Link to={item.to} key={item.label} className="header-shortcut">
                  <Icon size={29} strokeWidth={1.7} />
                  <strong>{item.label}</strong>
                  <span>{item.note}</span>
                </Link>
              ))}
            </div>
            <button className="menu-button" onClick={() => setMenuOpen((value) => !value)} aria-label="Mở menu">
              {menuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
        <nav className={`mainnav ${menuOpen ? 'mainnav--open' : ''}`}>
          <div className="container mainnav__inner">
            {mainNav.map((item) => (
              <NavLink key={item.label} to={item.to} className={isNavActive(item.to) ? 'active' : ''} onClick={() => setMenuOpen(false)}>
                {item.to === '/' && <Home size={18} />}{item.label}
              </NavLink>
            ))}
            {/* <button className="mainnav__search" onClick={() => setSearchOpen(true)}><Search size={16} /><span>Tìm kiếm...</span></button>
            <Link className="mainnav__admission" to="/tuyen-sinh">TUYỂN SINH</Link> */}
          </div>
        </nav>
      </header>
      <main><Outlet /></main>
      <footer className="footer">
        <div className="container footer__grid">
          <div className="footer__brand">
            <SchoolLogo compact name={schoolName} slogan={schoolSlogan} logoUrl={displayLogoUrl} />
            <p>{footerDescription}</p>
            <div className="socials"><span>f</span><span>▶</span><span>✉</span></div>
          </div>
          {footerColumns.map((column) => (
            <div key={column.title}>
              <h4>{column.title}</h4>
              {column.links?.map((item) => <Link to={item.url} key={`${column.title}-${item.label}`}>{item.label}</Link>)}
            </div>
          ))}
          <div className="footer__contact"><h4>LIÊN HỆ</h4><p><MapPin size={17} />{schoolAddress}</p><p><Phone size={17} />{schoolPhone}</p><p><Mail size={17} />{schoolEmail}</p><p><CalendarDays size={17} />{workingHours}</p></div>
        </div>
        <div className="footer__bottom"><div className="container"><span>{footerCopyright}</span><span><a href="https://ndhoa.dedyn.io" target="_blank" rel="noreferrer">Thiết kế bởi NDHoa</a></span></div></div>
      </footer>
      {searchOpen && (
        <div className="search-overlay" role="dialog" aria-modal="true">
          <button onClick={() => setSearchOpen(false)} aria-label="Đóng"><X /></button>
          <form onSubmit={submitSearch}><Search /><input autoFocus value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Nhập nội dung cần tìm..." /><button type="submit">Tìm kiếm</button></form>
        </div>
      )}
      <ChatBot site={site} />
    </div>
  )
}

function PageLoadingOverlay({
  active,
  authority,
  name,
  slogan,
  logoUrl,
}: {
  active: boolean
  authority: string
  name: string
  slogan: string
  logoUrl?: string
}) {
  const [visible, setVisible] = useState(true)
  const [isLeaving, setIsLeaving] = useState(false)

  useEffect(() => {
    if (active) {
      setVisible(true)
      setIsLeaving(false)
      return undefined
    }

    const leaveTimer = window.setTimeout(() => setIsLeaving(true), loadingHideDelayMs)
    const removeTimer = window.setTimeout(() => setVisible(false), loadingHideDelayMs + 320)

    return () => {
      window.clearTimeout(leaveTimer)
      window.clearTimeout(removeTimer)
    }
  }, [active])

  if (!visible) return null

  return (
    <div className={`page-loading ${isLeaving ? 'page-loading--leaving' : 'page-loading--active'}`} role="status" aria-live="polite" aria-label="Đang tải dữ liệu">
      <div className="page-loading__content">
        <SchoolLogo authority={authority} name={name} slogan={slogan} logoUrl={logoUrl} />
        <div className="page-loading__bar" aria-hidden="true"><span /></div>
        <p>Đang tải dữ liệu...</p>
      </div>
    </div>
  )
}
